import { Box, Button, type SxProps, type Theme, Stack, styled, useTheme } from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import React, { useState } from 'react';
import { AlertTriangleBoldIcon, InformationCircleIconBold } from 'src/icons';
import { CheckCircle1Icon } from 'src/icons/bold';
import { Link } from './Link';
import { Typography } from './Typography';

export type AlertVariant = 'success' | 'info' | 'warning' | 'critical';

export interface AlertProps {
    title?: string;
    children?: React.ReactNode;
    action?: React.ReactNode;
    variant: AlertVariant;
    storageKey?: string;
    onDismiss?: () => void;
    dismissLabel?: string;
    link?: string;
    linkText?: string;
    iconColorOverride?: string;
    sx?: SxProps<Theme>;
}

const CloseButton = styled(Button)(({ theme }) => ({
    lineHeight: 0,
    padding: 0,
    minWidth: 0,
    alignSelf: 'flex-start',
    color: theme.resqPalette.icon.default,
}));

export const Alert = ({
    title,
    children,
    action,
    variant,
    storageKey,
    link,
    linkText,
    onDismiss,
    dismissLabel,
    iconColorOverride,
    sx,
}: AlertProps) => {
    const theme = useTheme();
    const alertDismissed = (storageKey && Boolean(sessionStorage.getItem(storageKey))) || false;
    const [isDismissed, setDismissed] = useState(alertDismissed);

    const dismissAlert = () => {
        if (!storageKey) return;
        setDismissed(true);
        sessionStorage.setItem(storageKey, 'true');
    };

    let borderColor;
    let backgroundColor;
    let iconColor;
    let Icon;
    switch (variant) {
        case 'success':
            borderColor = theme.resqPalette.border.success;
            backgroundColor = theme.resqPalette.surface.successSubdued;
            iconColor = theme.resqPalette.icon.success;
            Icon = CheckCircle1Icon;
            break;
        case 'info':
            borderColor = theme.resqPalette.border.highlight;
            backgroundColor = theme.resqPalette.surface.highlightSubdued;
            iconColor = theme.resqPalette.icon.highlight;
            Icon = InformationCircleIconBold;
            break;
        case 'warning':
            borderColor = theme.resqPalette.border.warning;
            backgroundColor =
                theme.palette.mode === 'dark'
                    ? 'rgba(235, 189, 84, 0.16)'
                    : theme.resqPalette.surface.warningSubdued;
            iconColor = theme.resqPalette.icon.warning;
            Icon = AlertTriangleBoldIcon;
            break;
        case 'critical':
            borderColor = theme.resqPalette.border.critical;
            backgroundColor = theme.resqPalette.surface.criticalSubdued;
            iconColor = theme.resqPalette.icon.critical;
            Icon = AlertTriangleBoldIcon;
            break;
    }

    return isDismissed ? null : (
        <Box
            display="flex"
            gap={1}
            flex={1}
            sx={{
                border: `1px solid ${borderColor}`,
                backgroundColor: backgroundColor,
                borderRadius: '4px',
                paddingX: theme.spacing(2),
                paddingY: theme.spacing(2),
                ...(action && { flexDirection: { xs: 'column', md: 'row' }, gap: 2 }),
                ...sx,
            }}
        >
            <Box display="flex" gap={1} flex={1}>
                <Box display="flex" flexShrink={0}>
                    <Icon
                        width={18}
                        height={title ? 20 : 18}
                        fill={iconColorOverride ?? iconColor}
                    />
                </Box>
                <Stack spacing={0.5}>
                    {title && (
                        <Typography
                            role="label-bold"
                            color={variant === 'critical' ? 'critical' : 'default'}
                        >
                            {title}
                        </Typography>
                    )}
                    <Typography
                        role="label-small"
                        color={variant === 'critical' ? 'critical' : 'default'}
                    >
                        {children}
                    </Typography>
                    {linkText && (
                        <Link to={link} sx={{ marginTop: '8px' }}>
                            <Typography role="label-small" color="interactive">
                                {linkText}
                            </Typography>
                        </Link>
                    )}
                </Stack>
            </Box>
            {action && (
                <Box flexShrink={0} alignSelf={{ xs: 'flex-start', md: 'center' }}>
                    {action}
                </Box>
            )}
            {(storageKey || onDismiss) && (
                <CloseButton
                    aria-label={dismissLabel}
                    color="primary"
                    disableRipple={true}
                    disableElevation={true}
                    onClick={() => {
                        dismissAlert();
                        if (onDismiss) onDismiss();
                    }}
                >
                    <ClearIcon />
                </CloseButton>
            )}
        </Box>
    );
};
